/**
 * UTM Campaign Tracking Service
 *
 * Handles capture, storage, and retrieval of UTM parameters for email campaign attribution.
 * Provides direct campaign impact measurement with support for recovery scenarios (password reset, etc.).
 *
 * Storage Strategy:
 * 1. sessionStorage: Primary storage for same-session login (direct campaign conversion)
 * 2. localStorage: Fallback for recovery scenarios (password reset, failed auth attempts)
 * 3. 24-hour TTL: Prevents stale attribution from old campaigns
 */

import { logAppError } from '@/config/sentryConfig';

/** Storage keys */
const UTM_STORAGE_KEY = 'utm_params';
const UTM_STORAGE_KEY_LOCAL = 'utm_params_backup';

/** Time window for attribution from localStorage (in hours) */
const ATTRIBUTION_WINDOW_HOURS = 24;

/**
 * UTM data structure with timestamp for time-gated attribution
 */
export interface UTMData {
  /** UTM parameters extracted from URL (utm_source, utm_medium, etc.) */
  params: Record<string, string>;
  /** Unix epoch timestamp (milliseconds) when campaign link was clicked */
  timestamp: number;
}

/**
 * Capture and store UTM parameters from URL
 *
 * Called during initial page load to preserve campaign data before any redirects.
 * Stores params in storage for login-time retrieval.
 * Stores in both sessionStorage (primary) and localStorage (backup for recovery scenarios).
 *
 * @param {Record<string, unknown>} query - URL query object from router (to.query)
 * @returns {void}
 *
 * @example
 * // In router guard:
 * import { captureUTMParams } from '@/services/utm/utmTrackingService';
 * captureUTMParams(to.query);
 */
export const captureUTMParams = (query: Record<string, unknown>): void => {
  // Match any utm_-prefixed key (utm_source, utm_id, future ESP variants, etc.)
  // rather than a fixed whitelist, so new campaign params need no code change.
  const utmParams: Record<string, string> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (key.startsWith('utm_') && value && typeof value === 'string') {
      utmParams[key] = value;
    }
  });

  // Only process if UTM parameters are present
  if (Object.keys(utmParams).length) {
    const utmData: UTMData = {
      params: utmParams,
      timestamp: Date.now()
    };

    // Store in sessionStorage for immediate use (same-session login)
    // If user logs in during this session, we'll send login event with campaign data
    try {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    } catch (e) {
      // Private browsing or quota exceeded - log but continue
      // sessionStorage not available, but localStorage fallback may still work
      logAppError({
        error: e,
        functionName: 'captureUTMParams',
        section: 'sessionStorage_primary',
        level: 'warning',
        message: 'Failed to write UTM parameters to sessionStorage'
      });
    }

    // Backup storage for recovery scenarios (password reset, auth failures)
    // Allows attribution even if session is lost, with 24-hour TTL enforcement
    try {
      localStorage.setItem(UTM_STORAGE_KEY_LOCAL, JSON.stringify(utmData));
    } catch (e) {
      // Private browsing or quota exceeded - log but continue
      // If both storages fail, attribution data cannot be persisted for login-time use
      logAppError({
        error: e,
        functionName: 'captureUTMParams',
        section: 'localStorage_backup',
        level: 'warning',
        message: 'Failed to write UTM parameters to localStorage'
      });
    }
  }
};

/**
 * Retrieve UTM parameters for campaign attribution
 *
 * Implements fallback strategy:
 * 1. Try sessionStorage first (same-session = direct campaign conversion)
 * 2. Fall back to localStorage if session empty (recovery scenarios like password reset)
 * 3. Validate time window (24 hours) to prevent stale attribution
 * 4. Return null if not found or expired
 *
 * After retrieval, caller is responsible for cleanup via clearUTMParams().
 *
 * @returns {UTMData | null} UTM data if found and valid, null otherwise
 *
 * @example
 * // In login/auth flow:
 * import { getUTMParams } from '@/services/utm/utmTrackingService';
 * const utmData = getUTMParams();
 * if (utmData && utmData.params) {
 *   trackingEvents.user(userId, tenants, language, utmData.params); // include attribution in User event
 *   clearUTMParams(); // cleanup after processing
 * }
 */
export const getUTMParams = (): UTMData | null => {
  // STEP 1: Try sessionStorage (primary - same-session login)
  // Direct campaign attribution: user clicked link → landed → logged in (all in one session)
  let sessionUtm: string | null = null;
  try {
    sessionUtm = sessionStorage.getItem(UTM_STORAGE_KEY);
  } catch (e) {
    logAppError({
      error: e,
      functionName: 'getUTMParams',
      section: 'sessionStorage_read',
      level: 'warning',
      message: 'Failed to read UTM parameters from sessionStorage'
    });
  }

  if (sessionUtm) {
    try {
      const parsedData = JSON.parse(sessionUtm) as UTMData;
      const ageInHours = (Date.now() - parsedData.timestamp) / (1000 * 60 * 60);
      if (ageInHours <= ATTRIBUTION_WINDOW_HOURS) {
        return parsedData;
      }

      // Expired: cleanup so we don't keep re-processing stale attribution
      try {
        sessionStorage.removeItem(UTM_STORAGE_KEY);
      } catch (clearError) {
        logAppError({
          error: clearError,
          functionName: 'getUTMParams',
          section: 'sessionStorage_cleanup_expired',
          level: 'warning',
          message: 'Failed to clear expired session UTM data'
        });
      }
    } catch (e) {
      logAppError({
        error: e,
        functionName: 'getUTMParams',
        section: 'sessionStorage_parse',
        level: 'warning',
        message: 'Failed to parse session UTM data'
      });

      // Invalid data: cleanup to avoid repeated parse errors
      try {
        sessionStorage.removeItem(UTM_STORAGE_KEY);
      } catch (clearError) {
        logAppError({
          error: clearError,
          functionName: 'getUTMParams',
          section: 'sessionStorage_cleanup_invalid',
          level: 'warning',
          message: 'Failed to clear invalid session UTM data'
        });
      }
    }
  }

  // STEP 2: Fallback to localStorage (recovery scenarios)
  // Handles cases where session was lost but user returns within time window:
  // - Password reset flow (user closes browser during reset email)
  // - Failed auth attempt followed by successful retry
  // - Browser closed and reopened (session storage cleared)
  let localUtm: string | null = null;
  try {
    localUtm = localStorage.getItem(UTM_STORAGE_KEY_LOCAL);
  } catch (e) {
    logAppError({
      error: e,
      functionName: 'getUTMParams',
      section: 'localStorage_read',
      level: 'warning',
      message: 'Failed to read UTM parameters from localStorage'
    });
  }

  if (localUtm) {
    try {
      const parsedData = JSON.parse(localUtm) as UTMData;

      // Calculate time elapsed since campaign click (in hours)
      const ageInHours = (Date.now() - parsedData.timestamp) / (1000 * 60 * 60);

      // Only use if within attribution window
      // This prevents stale attribution: a login days/weeks later
      // shouldn't be credited to an old campaign
      if (ageInHours <= ATTRIBUTION_WINDOW_HOURS) {
        return parsedData;
      }

      // Expired: cleanup so we don't keep re-processing stale attribution
      try {
        localStorage.removeItem(UTM_STORAGE_KEY_LOCAL);
      } catch (clearError) {
        logAppError({
          error: clearError,
          functionName: 'getUTMParams',
          section: 'localStorage_cleanup_expired',
          level: 'warning',
          message: 'Failed to clear expired localStorage UTM data'
        });
      }
    } catch (e) {
      logAppError({
        error: e,
        functionName: 'getUTMParams',
        section: 'localStorage_parse',
        level: 'warning',
        message: 'Failed to parse localStorage UTM data'
      });

      // Invalid data: cleanup to avoid repeated parse errors
      try {
        localStorage.removeItem(UTM_STORAGE_KEY_LOCAL);
      } catch (clearError) {
        logAppError({
          error: clearError,
          functionName: 'getUTMParams',
          section: 'localStorage_cleanup_invalid',
          level: 'warning',
          message: 'Failed to clear invalid localStorage UTM data'
        });
      }
    }
  }

  return null;
};

/**
 * Clear UTM parameters from all storage locations
 *
 * Should be called after sending UTM data to Mixpanel to prevent false attribution
 * on subsequent logins from the same device.
 *
 * @returns {void}
 *
 * @example
 * // After sending login event:
 * import { clearUTMParams } from '@/services/utm/utmTrackingService';
 * trackingEvents.login(utmParams);
 * clearUTMParams(); // Cleanup
 */
export const clearUTMParams = (): void => {
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
  } catch (e) {
    logAppError({
      error: e,
      functionName: 'clearUTMParams',
      section: 'sessionStorage_clear',
      level: 'warning',
      message: 'Failed to clear UTM parameters from sessionStorage'
    });
  }

  try {
    localStorage.removeItem(UTM_STORAGE_KEY_LOCAL);
  } catch (e) {
    logAppError({
      error: e,
      functionName: 'clearUTMParams',
      section: 'localStorage_clear',
      level: 'warning',
      message: 'Failed to clear UTM parameters from localStorage'
    });
  }
};
